import { useEffect, useState } from "react";
import { Users, CheckCircle2, BedDouble, CalendarClock, Download, Trash2, MessageCircle, Search, Music, Send, History } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listRsvps, rsvpStats, rsvpPlanning, deleteRsvp, updateRsvp, downloadCsv } from "@/lib/api";
import { StayPlanner } from "@/components/wedding/StayPlanner";

const digits = (num) => (num || "").replace(/[^\d]/g, "");
const waLink = (num) => `https://wa.me/${digits(num)}`;

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[var(--card)] rounded-xl p-5 border border-[var(--border)]" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="rounded-lg p-2.5" style={{ backgroundColor: "var(--rose)" }}>
        <Icon className="w-5 h-5 text-[var(--burgundy)]" />
      </div>
      <div>
        <p className="text-2xl font-serif text-[var(--burgundy)]">{value}</p>
        <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide">{label}</p>
      </div>
    </div>
  </div>
);

export const RsvpDashboard = () => {
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [planning, setPlanning] = useState(null);
  const [q, setQ] = useState("");
  const [accFilter, setAccFilter] = useState("all");
  const [attFilter, setAttFilter] = useState("all");
  const [manageFor, setManageFor] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("pending");

  const load = () => {
    listRsvps().then(setRsvps).catch(() => {});
    rsvpStats().then(setStats).catch(() => {});
    rsvpPlanning().then(setPlanning).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await deleteRsvp(id);
    toast.success("RSVP deleted");
    load();
  };

  const openManage = (r) => {
    setManageFor(r);
    setNoteDraft(r.accommodation_note || "");
    setStatusDraft(r.accommodation_status || "pending");
  };

  const saveStay = async () => {
    await updateRsvp(manageFor.id, { accommodation_status: statusDraft, accommodation_note: noteDraft });
    toast.success("Stay details saved");
    setManageFor(null);
    load();
  };

  const shareStay = async () => {
    const r = manageFor;
    await updateRsvp(r.id, { accommodation_status: statusDraft, accommodation_note: noteDraft });
    load();
    const statusLine = statusDraft === "confirmed" ? "Your stay is CONFIRMED ✅" : "We're arranging your stay.";
    const arrival = r.arrival_date ? ` Arrival: ${r.arrival_date}${r.arrival_time ? ` (${r.arrival_time})` : ""}.` : "";
    const details = noteDraft ? ` Details: ${noteDraft}.` : "";
    const msg = `Hi ${r.name}! 💛 For Rishi & Priyanka's wedding — ${statusLine}${arrival}${details} See you soon!`;
    window.open(`${waLink(r.whatsapp)}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const filtered = rsvps.filter((r) => {
    if (q && !`${r.name} ${r.whatsapp}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (accFilter === "yes" && !r.needs_accommodation) return false;
    if (accFilter === "no" && r.needs_accommodation) return false;
    if (attFilter !== "all" && r.attending !== attFilter) return false;
    return true;
  });

  return (
    <div className="space-y-8" data-testid="cms-rsvp-dashboard">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={Users} label="Total Guests" value={stats.total_guests} testid="stat-total-guests" />
          <Stat icon={CheckCircle2} label="Accepted" value={stats.accepted} testid="stat-accepted" />
          <Stat icon={BedDouble} label="Need Stay" value={stats.accommodation_guests} testid="stat-accommodation" />
          <Stat icon={Music} label="Open Mic" value={stats.open_mic} testid="stat-openmic" />
          <Stat icon={CalendarClock} label="Responses" value={stats.total_responses} testid="stat-responses" />
        </div>
      )}

      <StayPlanner planning={planning} />

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or WhatsApp" className="pl-9" data-testid="cms-rsvp-search" />
        </div>
        <Select value={accFilter} onValueChange={setAccFilter}>
          <SelectTrigger className="sm:w-52" data-testid="cms-accommodation-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accommodation</SelectItem>
            <SelectItem value="yes">Needs accommodation</SelectItem>
            <SelectItem value="no">No accommodation</SelectItem>
          </SelectContent>
        </Select>
        <Select value={attFilter} onValueChange={setAttFilter}>
          <SelectTrigger className="sm:w-44" data-testid="cms-attendance-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All responses</SelectItem>
            <SelectItem value="yes">Accepted</SelectItem>
            <SelectItem value="no">Declined</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={downloadCsv} className="rounded-lg px-4 py-2 text-sm text-white flex items-center gap-2 justify-center" style={{ backgroundColor: "var(--burgundy)" }} data-testid="cms-export-csv">
          <Download className="w-4 h-4" /> CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-[var(--card)] rounded-xl border border-[var(--border)] overflow-x-auto" data-testid="cms-rsvp-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--text-muted)] text-xs uppercase tracking-wide border-b border-[var(--border)]">
              <th className="p-4">Guest</th>
              <th className="p-4">WhatsApp</th>
              <th className="p-4">Status</th>
              <th className="p-4">Party</th>
              <th className="p-4">Accommodation</th>
              <th className="p-4">Arrival / Departure</th>
              <th className="p-4">Prep notes</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[var(--text-muted)]" data-testid="cms-rsvp-empty">No RSVPs yet.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface)] align-top" data-testid={`cms-rsvp-row-${r.id}`}>
                <td className="p-4">
                  <p className="font-medium text-[var(--text-primary)]">{r.name}</p>
                  {r.join_open_mic && (
                    <span className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--gold-hover)]" data-testid={`cms-openmic-${r.id}`}>
                      <Music className="w-3 h-3" /> {r.contribution || "Open mic"}
                    </span>
                  )}
                  {r.edit_count > 0 && (
                    <button onClick={() => setAuditFor(r)} className="mt-1 flex items-center gap-1 text-xs text-[var(--gold-hover)] underline" data-testid={`cms-audit-btn-${r.id}`}>
                      <History className="w-3 h-3" /> Updated {r.edit_count}× · view history
                    </button>
                  )}
                </td>
                <td className="p-4">
                  <a href={waLink(r.whatsapp)} target="_blank" rel="noreferrer" className="text-[var(--text-primary)] hover:text-[var(--gold-hover)] underline decoration-dotted" data-testid={`cms-whatsapp-link-${r.id}`}>
                    {r.whatsapp}
                  </a>
                </td>
                <td className="p-4">
                  <Badge className={r.attending === "yes" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>
                    {r.attending === "yes" ? "Accepts" : "Declines"}
                  </Badge>
                </td>
                <td className="p-4">{r.party_size}</td>
                <td className="p-4">
                  {r.needs_accommodation ? (
                    <div className="space-y-1">
                      <Badge style={{ backgroundColor: "var(--gold)" }} className="text-white hover:opacity-90">Yes</Badge>
                      <div>
                        <Badge variant="outline" className={r.accommodation_status === "confirmed" ? "border-green-500 text-green-700" : "border-[var(--border-gold)] text-[var(--gold-hover)]"} data-testid={`cms-stay-status-${r.id}`}>
                          {r.accommodation_status === "confirmed" ? "Confirmed" : "Pending"}
                        </Badge>
                      </div>
                    </div>
                  ) : <span className="text-[var(--text-muted)]">—</span>}
                </td>
                <td className="p-4">
                  {r.arrival_date || r.departure_date ? (
                    <div className="text-sm leading-tight">
                      {r.arrival_date ? (
                        <div className="text-[var(--text-primary)]">
                          <span className="text-[var(--text-muted)]">In:</span> {r.arrival_date}
                          {r.arrival_time ? <span className="block text-xs text-[var(--text-muted)]">{r.arrival_time}</span> : null}
                        </div>
                      ) : null}
                      {r.departure_date ? (
                        <div className="text-[var(--text-secondary)] mt-0.5">
                          <span className="text-[var(--text-muted)]">Out:</span> {r.departure_date}
                        </div>
                      ) : null}
                    </div>
                  ) : <span className="text-[var(--text-muted)]">—</span>}
                </td>
                <td className="p-4 max-w-[220px]">
                  {r.dietary && <p className="text-xs"><span className="text-[var(--text-muted)]">Diet:</span> {r.dietary}</p>}
                  {r.prep_notes && <p className="text-xs text-[var(--text-secondary)]">{r.prep_notes}</p>}
                  {r.custom && Object.entries(r.custom).map(([k, v]) => v ? (
                    <p key={k} className="text-xs text-[var(--text-secondary)]"><span className="text-[var(--text-muted)]">{k}:</span> {v}</p>
                  ) : null)}
                  {r.notes && <p className="text-xs text-[var(--text-muted)] italic">"{r.notes}"</p>}
                </td>
                <td className="p-4">
                  <div className="flex gap-1">
                    <a href={waLink(r.whatsapp)} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-[var(--rose)]" title="Chat on WhatsApp" data-testid={`cms-whatsapp-btn-${r.id}`}>
                      <MessageCircle className="w-4 h-4 text-green-600" />
                    </a>
                    {r.needs_accommodation && (
                      <button onClick={() => openManage(r)} className="p-2 rounded hover:bg-[var(--rose)]" title="Manage stay" data-testid={`cms-manage-stay-${r.id}`}>
                        <BedDouble className="w-4 h-4 text-[var(--gold-hover)]" />
                      </button>
                    )}
                    <button onClick={() => remove(r.id)} className="p-2 rounded hover:bg-red-50" title="Delete" data-testid={`cms-delete-btn-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Manage stay dialog */}
      <Dialog open={!!manageFor} onOpenChange={(o) => !o && setManageFor(null)}>
        <DialogContent data-testid="cms-manage-stay-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[var(--burgundy)]">Manage Stay — {manageFor?.name}</DialogTitle>
          </DialogHeader>
          {manageFor && (
            <div className="space-y-4 text-sm">
              <p className="text-[var(--text-secondary)]">
                Party of {manageFor.party_size}
                {manageFor.arrival_date ? ` · Arrival ${manageFor.arrival_date} ${manageFor.arrival_time || ""}` : ""}
                {manageFor.departure_date ? ` · Departs ${manageFor.departure_date}` : ""}
              </p>
              <div>
                <Label>Status</Label>
                <Select value={statusDraft} onValueChange={setStatusDraft}>
                  <SelectTrigger className="mt-1.5" data-testid="cms-stay-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Booking details to share (hotel, room, check-in...)</Label>
                <Textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="e.g., Lake View Resort, Deluxe Room #204, check-in 11 Dec 2 PM. Booking ref RP-1234." className="mt-1.5" data-testid="cms-stay-note-input" />
              </div>
              <div className="flex flex-wrap gap-3 pt-1">
                <button onClick={saveStay} className="rounded-full px-5 py-2.5 text-white font-medium" style={{ backgroundColor: "var(--gold)" }} data-testid="cms-stay-save">
                  Save
                </button>
                <button onClick={shareStay} className="rounded-full px-5 py-2.5 text-white font-medium flex items-center gap-2" style={{ backgroundColor: "#25D366" }} data-testid="cms-stay-share-whatsapp">
                  <Send className="w-4 h-4" /> Save & Share on WhatsApp
                </button>
              </div>
              <p className="text-xs text-[var(--text-muted)] italic">Tip: Save first, then Share — WhatsApp opens with a ready-to-send message to the guest.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit history dialog */}
      <Dialog open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)}>
        <DialogContent data-testid="cms-audit-modal" className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[var(--burgundy)]">Edit history — {auditFor?.name}</DialogTitle>
          </DialogHeader>
          {auditFor && (
            <div className="space-y-4 text-sm">
              <p className="text-[var(--text-secondary)]">
                Updated {auditFor.edit_count} time{auditFor.edit_count === 1 ? "" : "s"}.
                {auditFor.updated_at ? ` Last updated ${new Date(auditFor.updated_at).toLocaleString()}.` : ""}
                {" "}Showing previous versions (most recent first).
              </p>
              {[...(auditFor.history || [])].reverse().map((h, i) => (
                <div key={i} className="rounded-lg border border-[var(--border)] p-3 bg-[var(--surface)]" data-testid={`cms-audit-entry-${i}`}>
                  <p className="text-xs text-[var(--text-muted)]">Version saved {h.changed_at ? new Date(h.changed_at).toLocaleString() : "—"}</p>
                  <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                    <span><b>Name:</b> {h.name || "—"}</span>
                    <span><b>WhatsApp:</b> {h.whatsapp || "—"}</span>
                    <span><b>Attending:</b> {h.attending === "yes" ? "Accepts" : "Declines"}</span>
                    <span><b>Party:</b> {h.party_size}</span>
                    <span><b>Accommodation:</b> {h.needs_accommodation ? "Yes" : "No"}</span>
                    <span><b>Arrival:</b> {h.arrival_date || "—"} {h.arrival_time || ""}</span>
                    <span><b>Dietary:</b> {h.dietary || "—"}</span>
                    <span><b>Open mic:</b> {h.join_open_mic ? (h.contribution || "Yes") : "No"}</span>
                    <span className="col-span-2"><b>Prep notes:</b> {h.prep_notes || "—"}</span>
                    <span className="col-span-2"><b>Message:</b> {h.notes || "—"}</span>
                  </div>
                </div>
              ))}
              {(!auditFor.history || auditFor.history.length === 0) && (
                <p className="text-[var(--text-muted)]">No previous versions recorded.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
